import { Heart, HelpCircle, User } from 'lucide-react'
import { Fragment } from 'react'

import { FullPageSpinnerPortal } from '@/components/common/Spinner/FullPagePortal'
import { FieldError } from '@/components/ui/field'
import { cn } from '@/lib/utils'

import { CheckinLayout } from './CheckinLayout'
import { StepHeader } from './StepHeader'

interface ManualStepProps {
  onBack: () => void
  form: any
  submitError: string
  isCheckinPending: boolean
}

export function ManualStep({
  onBack,
  form,
  submitError,
  isCheckinPending,
}: ManualStepProps) {
  return (
    <Fragment>
      <CheckinLayout>
        <StepHeader
          title="ลงทะเบียนด้วยตนเอง"
          onBack={onBack}
        />

        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-6 pt-2 pb-6 text-left"
        >
          {submitError && (
            <p className="text-destructive bg-destructive/10 rounded-lg p-3 text-center text-sm font-medium">
              {submitError}
            </p>
          )}

          <form.AppField name="side">
            {(field: any) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid
              const options = [
                {
                  value: 'groom',
                  label: 'ฝ่ายเจ้าบ่าว',
                  sublabel: 'แขกของ OA',
                  Icon: User,
                },
                {
                  value: 'bride',
                  label: 'ฝ่ายเจ้าสาว',
                  sublabel: 'แขกของ KOI',
                  Icon: Heart,
                },
              ]
              return (
                <div className="flex flex-col gap-1.5">
                  <p className="text-foreground text-sm font-semibold">
                    ฝ่าย *
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {options.map(({ value, label, sublabel, Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => field.handleChange(value)}
                        className={cn(
                          'flex flex-col items-center gap-1 rounded-2xl border-2 px-3 py-3 text-center transition-all active:scale-[0.98]',
                          field.state.value === value
                            ? 'border-primary bg-primary/10 shadow-md'
                            : 'border-white/60 bg-white/60 shadow-sm hover:bg-white/80',
                        )}
                      >
                        <Icon className="size-5" />
                        <span className="text-sm leading-tight font-semibold">
                          {label}
                        </span>
                        <span className="text-foreground/50 text-[10px] font-normal">
                          {sublabel}
                        </span>
                      </button>
                    ))}
                  </div>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </div>
              )
            }}
          </form.AppField>

          <form.AppField name="fullName">
            {(field: any) => (
              <field.TextField
                label="ชื่อเล่น *"
                placeholder="กรอกชื่อเล่น"
              />
            )}
          </form.AppField>

          <form.AppField name="displayName">
            {(field: any) => (
              <field.TextField
                label="ชื่อที่ต้องการให้แสดงในงาน *"
                placeholder="กรอกชื่อที่ต้องการให้แสดง"
              />
            )}
          </form.AppField>

          <form.AppField name="maritalStatus">
            {(field: any) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid
              const options = [
                { value: 'single', label: 'โสด', Icon: User },
                { value: 'not_single', label: 'ไม่โสด', Icon: Heart },
                {
                  value: 'prefer_not_to_say',
                  label: 'ไม่ระบุ',
                  Icon: HelpCircle,
                },
              ]
              return (
                <div className="flex flex-col gap-1.5">
                  <p className="text-foreground text-sm font-semibold">
                    สถานะ *
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {options.map(({ value, label, Icon }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => field.handleChange(value)}
                        className={cn(
                          'flex flex-col items-center gap-2 rounded-2xl border-2 px-2 py-3.5 transition-all active:scale-[0.98]',
                          field.state.value === value
                            ? 'border-primary bg-primary/10 shadow-md'
                            : 'border-white/60 bg-white/60 shadow-sm hover:bg-white/80',
                        )}
                      >
                        <Icon className="size-5" />
                        <span className="text-xs leading-tight font-semibold">
                          {label}
                        </span>
                      </button>
                    ))}
                  </div>
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </div>
              )
            }}
          </form.AppField>

          <form.AppField name="followers">
            {(field: any) => (
              <field.SelectField
                label="จำนวนผู้เข้าร่วม *"
                options={[
                  { value: '1', label: '1 ท่าน' },
                  { value: '2', label: '2 ท่าน' },
                  { value: '3', label: '3 ท่าน' },
                  { value: '4', label: '4 ท่าน' },
                ]}
              />
            )}
          </form.AppField>

          <div className="pt-4">
            <form.AppForm>
              <form.SubmitButton
                label="เช็กอิน"
                loadingLabel="กำลังดำเนินการ..."
              />
            </form.AppForm>
          </div>
        </form>
      </CheckinLayout>
      {(form.state.isSubmitting || isCheckinPending) && (
        <FullPageSpinnerPortal />
      )}
    </Fragment>
  )
}
